import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState, useContext } from "react";
import InputField from "views/admin/events/components/InputField";
import CategoryMultiSelect from "components/CategoryMultiSelect";
import { AppContext } from "contexts/AppContext";
import { addMenuCategory } from "Api/apiService";

const normalizeCategory = (value) => String(value || "").trim();
const toKey = (value) => normalizeCategory(value).toLowerCase();

export default function BrunchMenuItemModal({
  isOpen,
  onClose,
  selectedMenuItem,
  updateMenuItem,
  onDeleteMenuItem,
}) {
  const [itemName, setItemName] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [itemPrice, setItemPrice] = useState(null);

  const [itemNameError, setItemNameError] = useState("");
  const [itemDescriptionError, setItemDescriptionError] = useState("");
  const [itemPriceError, setItemPriceError] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState("");

  const textColor = useColorModeValue("navy.700", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");

  const { brunchMenuCategories, fetchBrunchMenuCategories } =
    useContext(AppContext);

  const categoryOptions = useMemo(
    () => (brunchMenuCategories || []).map((category) => category.name),
    [brunchMenuCategories]
  );

  const handleItemNameChange = (e) => {
    setItemName(e.target.value);
  };

  const handleItemDescriptionChange = (e) => {
    setItemDescription(e.target.value);
  };
  const handleItemPriceChange = (e) => {
    setItemPrice(e.target.value);
  };

  useEffect(() => {
    if (isOpen) {
      setItemPrice(selectedMenuItem?.itemPrice);
      setItemName(selectedMenuItem?.itemName);
      setItemDescription(selectedMenuItem?.itemDescription);
      setSelectedCategories(selectedMenuItem?.itemCategory || []);
      setCategoryInput("");
      fetchBrunchMenuCategories();
    } else {
      setItemPrice(null);
      setItemName("");
      setItemDescription("");
      setSelectedCategories([]);
      setCategoryInput("");
      setItemNameError("");
      setItemPriceError("");
      setItemDescriptionError("");
    }
  }, [isOpen, selectedMenuItem]);

  const resolveCategories = async () => {
    const trimmedInput = normalizeCategory(categoryInput);
    let nextSelected = selectedCategories;

    if (trimmedInput) {
      const inputKey = toKey(trimmedInput);
      const hasSelected = selectedCategories.some(
        (category) => toKey(category) === inputKey
      );
      if (!hasSelected) {
        nextSelected = [...selectedCategories, trimmedInput];
      }

      const hasOption = categoryOptions.some(
        (category) => toKey(category) === inputKey
      );
      if (!hasOption) {
        try {
          await addMenuCategory({ type: "brunch", name: trimmedInput });
          fetchBrunchMenuCategories();
        } catch (error) {
          console.error("Error adding brunch category:", error);
        }
      }
    }

    return nextSelected;
  };

  const handleSave = async () => {
    try {
      let isValid = true;

      if (!itemName) {
        setItemNameError("Name is required.");
        isValid = false;
      } else {
        setItemNameError("");
      }

      if (!itemDescription) {
        setItemDescriptionError("Description is required.");
        isValid = false;
      } else {
        setItemDescriptionError("");
      }

      if (!itemPrice) {
        setItemPriceError("Price is required.");
        isValid = false;
      } else {
        setItemPriceError("");
      }

      if (!isValid) {
        return;
      }

      const normalizedPrice = Number(itemPrice);
      const resolvedCategories = await resolveCategories();
      setSelectedCategories(resolvedCategories);
      setCategoryInput("");

      updateMenuItem(
        selectedMenuItem._id,
        itemName,
        resolvedCategories,
        itemDescription,
        normalizedPrice
      );

      onClose();
    } catch (error) {
      console.error("Error updating brunch menu item:", error);
    }
  };

  const handleDelete = async () => {
    if (!selectedMenuItem?._id) {
      return;
    }
    try {
      await onDeleteMenuItem(selectedMenuItem._id);
      onClose();
    } catch (error) {
      console.error("Error deleting brunch menu item:", error);
    }
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader flex={1} textAlign="center">
          Brunch Menu Item Details
        </ModalHeader>

        <ModalCloseButton />
        <ModalBody>
          <InputField
            label="Brunch Item Name"
            type="text"
            value={itemName}
            onChange={handleItemNameChange}
            errorMessage={itemNameError}
            isRequired={true}
          />

          <FormControl isInvalid={!!itemDescriptionError}>
            <FormLabel
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
            >
              <Flex align="center">
                Item Description<Text color={brandStars}>*</Text>
              </Flex>
            </FormLabel>
            <Textarea
              isInvalid={itemDescriptionError ? true : false}
              isRequired={true}
              fontSize="sm"
              size="lg"
              value={itemDescription}
              onChange={handleItemDescriptionChange}
              mb={itemDescriptionError ? "5px" : "24px"}
            />
            {itemDescriptionError && (
              <Text
                mb="14px"
                color="red.500"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
              >
                {itemDescriptionError}
              </Text>
            )}
          </FormControl>
          <InputField
            label="Brunch Item Price"
            type="number"
            value={itemPrice}
            onChange={handleItemPriceChange}
            errorMessage={itemPriceError}
            isRequired={true}
          />

          <CategoryMultiSelect
            label="Item Category"
            options={categoryOptions}
            selected={selectedCategories}
            onSelectedChange={setSelectedCategories}
            inputValue={categoryInput}
            onInputChange={setCategoryInput}
          />

          <Button
            fontSize="sm"
            variant="brand"
            fontWeight="500"
            w="100%"
            h="50"
            mb="24px"
            onClick={handleSave}
          >
            Save
          </Button>
          <Button
            fontSize="sm"
            variant="outline"
            colorScheme="red"
            w="100%"
            h="50"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
